import { useCallback, useEffect, useState } from "react";
import { ReportActivitySummaryResponse } from "../../../entities/reports/ReportActivitySummary/ReportActivitySummaryResponse";
import ConsolidatedService from "../../../services/ConsolidatedService";
import AdvancedFilterService, {
  Filter
} from "../../../services/AdvancedFilterService";
import FilterService, {
  FilterCollection
} from "../../../services/FilterService";
import { TranslationService } from "../../../services/TranslationService";
import { formatIntizaDate } from "../../../utils/FormatUtils";
import { sumList } from "../../../utils/Utils";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../../shared/components/AdvancedFilters";
import Table, { TableHeader } from "../../shared/Table";
import TableContext, { TableContextValues } from "../../task/TableContext";

export class ConsolidatedActivitySummaryTableContextValues extends TableContextValues<
  ReportActivitySummaryResponse,
  { extraFilters: string[] }
> {
  total = 0;
}

const ConsolidatedActivitySummary = () => {
  const { translate } = TranslationService;
  const [response, setResponse] = useState<ReportActivitySummaryResponse>(
    {} as ReportActivitySummaryResponse
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<string>(getDefaultFilter);

  const requestData = async () => {
    setLoading(true);
    const result = await ConsolidatedService.getActivitySummary({
      page: 0,
      filter,
      quickfilter: undefined
    });
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    setResponse(result);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, [filter]);

  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  const tableValues = new ConsolidatedActivitySummaryTableContextValues();
  const applyFilters = (filters: string[]) => {
    baseFilters = filters;
    setFilter(AdvancedFilterService.filterStringsToQueryString(filters));
  };

  tableValues.error = error;
  tableValues.response = response;
  tableValues.loading = loading;
  tableValues.reload = requestDataCallback;
  tableValues.total = 0;

  const headers: TableHeader[] = [
    new TableHeader("Usuario", translate.User),
    new TableHeader("Clientes", translate.ManagedCustomers, true, false),
    new TableHeader("Comentarios", translate.Comments, true, false),
    new TableHeader("Tareas", translate.Tasks, true, false),
    new TableHeader("Mails", translate.OutgoingEmails, true, false),
    new TableHeader("SMS", translate.SMS, true, false),
    new TableHeader("Whatsapp", translate.WhatsApp, true, false),
    new TableHeader("Total", translate.Total, true, false)
  ];

  return (
    <TableContext.Provider value={tableValues}>
      <div className="container-fluid padding">
        <div className="d-flex justify-content-between">
          <h2>{translate.ActivitySummary}</h2>
        </div>
        <div className="p-0">
          <div className="genericHeader">
            <div className="d-flex actionButtonsHeader">
              <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["Activity"]} />
            </div>
          </div>
          <div className="pt-0">
            <AdvancedFilters
              onFilterApply={applyFilters}
              page={FilterCollection.ReportActivityConsolidated}
              defaultValue={filter}
            />
          </div>
          <Table
            headers={headers}
            item={ConsolidatedActivitySummaryItem}
            stickyHeader
          >
            <ConsolidatedActivitySummaryTotal response={response} />
          </Table>
        </div>
      </div>
    </TableContext.Provider>
  );
};

let baseFilters: string[] = [];

const ConsolidatedActivitySummaryItem = ({
  data
}: {
  data: ReportActivitySummaryResponse.Item;
}) => {
  return (
    <tr>
      <td>{data.FullnameInv}</td>
      <td className="text-end">{data.Persons}</td>
      <td className="text-end">{data.Comments || "-"}</td>
      <td className="text-end">{data.Tasks || "-"}</td>
      <td className="text-end">{data.Mails || "-"}</td>
      <td className="text-end">{data.SMS || "-"}</td>
      <td className="text-end">{data.Whatsapp || "-"}</td>
      <td className="text-end">
        {data.Comments + data.Tasks + data.Mails + data.SMS + data.Whatsapp ||
          "-"}
      </td>
    </tr>
  );
};

const ConsolidatedActivitySummaryTotal = ({
  response
}: {
  response: ReportActivitySummaryResponse;
}) => {
  if ((response?.list?.length ?? 0) === 0) {
    return (
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    );
  }

  const users = sumList(response.list, (x) => x.Persons);
  const comments = sumList(response.list, (x) => x.Comments);
  const tasks = sumList(response.list, (x) => x.Tasks);
  const mails = sumList(response.list, (x) => x.Mails);
  const smss = sumList(response.list, (x) => x.SMS);
  const was = sumList(response.list, (x) => x.Whatsapp);
  const total = comments + tasks + mails + smss + was;

  return (
    <tr className="font-weight-bold">
      <td>{TranslationService.translate.Total}</td>
      <td className="text-end">{users}</td>
      <td className="text-end">{comments}</td>
      <td className="text-end">{tasks}</td>
      <td className="text-end">{mails}</td>
      <td className="text-end">{smss}</td>
      <td className="text-end">{was}</td>
      <td className="text-end">{total}</td>
    </tr>
  );
};

function getDefaultFilter() {
  const filterDefinitions = FilterService.GetFiltersForPage(
    FilterCollection.ReportActivityConsolidated
  ).flatMap((x) => x.definitions);
  const definition = filterDefinitions.find((x) => x.Field === "-100")!;
  const startDate = new Date();
  startDate.setDate(1);
  const endDate = new Date();
  endDate.setMonth(startDate.getMonth() + 1, 0);
  const dateFilter: Filter = {
    kind: definition.Type,
    definition,
    value: formatIntizaDate(startDate) + "-" + formatIntizaDate(endDate),
    operator: 0
  };

  const stringFilter = AdvancedFilterService.filterToString(dateFilter);
  baseFilters = [stringFilter];

  return FilterService.GetExtraFiltersRequestString(baseFilters) || "";
}

export default ConsolidatedActivitySummary;
